// LLM generated - Model Claude 4 Sonnet
import { format, intervalToDuration } from 'date-fns';
import { useEffect, useState } from 'react';

export type ExpirationCountdown = {
  value: string;
  hasDeadlinePassed: boolean;
  isApproachingDeadline: boolean;
};

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const formatExpirationValue = (target: Date, now: Date): string => {
  const remainingMs = target.getTime() - now.getTime();

  if (remainingMs >= 7 * MS_PER_DAY) {
    return format(target, 'MM/dd/yy');
  }

  if (remainingMs >= 2 * MS_PER_DAY) {
    return `${Math.floor(remainingMs / MS_PER_DAY)}d`;
  }

  if (remainingMs >= MS_PER_DAY) {
    const remHours = Math.floor((remainingMs - MS_PER_DAY) / MS_PER_HOUR);
    return `1d ${remHours}h`;
  }

  const duration = intervalToDuration({ start: now, end: target });
  return [
    String(duration.hours ?? 0).padStart(2, '0'),
    String(duration.minutes ?? 0).padStart(2, '0'),
    String(duration.seconds ?? 0).padStart(2, '0'),
  ].join(':');
};

export const useExpirationCountdown = (targetDate: Date): ExpirationCountdown => {
  const [state, setState] = useState<ExpirationCountdown>(() => ({
    value: '',
    hasDeadlinePassed: false,
    isApproachingDeadline: false,
  }));

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const tick = () => {
      const now = new Date();
      const remainingMs = targetDate.getTime() - now.getTime();

      if (remainingMs <= 0) {
        setState({ value: '', hasDeadlinePassed: true, isApproachingDeadline: true });
        return;
      }

      const value = formatExpirationValue(targetDate, now);
      const isApproachingDeadline = remainingMs < 2 * MS_PER_HOUR;
      setState({ value, hasDeadlinePassed: false, isApproachingDeadline });

      const nextDelay = remainingMs < MS_PER_DAY ? 1000 : 60_000;
      timeoutId = setTimeout(tick, nextDelay);
    };

    tick();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [targetDate]);

  return state;
};
